import { Lock } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';

/**
 * COMPATIBILITY SHIM — legacy ForbiddenState API over shadcn's Empty.
 *
 * The back-stack behaviour is ported verbatim: it decides both the CTA label
 * and the navigation target, so it is logic rather than styling.
 */

interface Props {
  title?: string;
  description?: string;
  ctaLabel?: string;
  onBack?: () => void;
  fullscreen?: boolean;
}

const DEFAULT_TITLE = "You don't have permission to view this.";
const DEFAULT_DESCRIPTION = 'Contact HR if you believe this is an error.';

function hasMeaningfulBackStack(): boolean {
  if (typeof window === 'undefined') return false;
  return window.history.length > 1;
}

export default function ForbiddenState({
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  ctaLabel,
  onBack,
  fullscreen = false,
}: Props) {
  const canGoBack = hasMeaningfulBackStack();
  const resolvedLabel = ctaLabel ?? (canGoBack ? 'Go back' : 'Go Home');
  const handleBack = onBack ?? (() => {
    if (canGoBack) { window.history.back(); }
    else { window.location.assign('/'); }
  });

  const card = (
    <Empty
      role="alert"
      className={cn(fullscreen ? 'max-w-[420px] border-none bg-transparent' : 'rounded-xl border bg-card')}
    >
      <EmptyHeader>
        <EmptyMedia
          variant="icon"
          className={cn(
            'bg-destructive/10 text-destructive',
            fullscreen && 'size-16 [&_svg:not([class*=size-])]:size-7',
          )}
        >
          <Lock />
        </EmptyMedia>
        <EmptyTitle className={cn('font-heading font-bold', fullscreen && 'text-[22px]')}>
          {title}
        </EmptyTitle>
        <EmptyDescription className={cn(fullscreen && 'text-15')}>
          {description}
        </EmptyDescription>
      </EmptyHeader>
      <EmptyContent>
        <Button onClick={handleBack} size={fullscreen ? 'lg' : 'default'}>
          {resolvedLabel}
        </Button>
      </EmptyContent>
    </Empty>
  );

  if (!fullscreen) return card;

  return (
    <div className="flex min-h-dvh items-center justify-center bg-background p-6">
      {card}
    </div>
  );
}
